import { Component, OnInit, inject } from '@angular/core';

import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AuthenticationService } from '../../services/authentication.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { CustomerSummary } from '../../models/customer-summary.model';
import { UserRoleValue } from '../../models/user-role.model';

interface FacilityNotification {
  facilityId: number;
  facilityName: string;
  enabled: boolean;
  locked: boolean;
}

interface CompanyNotification {
  companyId: number;
  companyName: string;
  allEnabled: boolean;
  facilities: FacilityNotification[];
}

@Component({
  selector: 'app-notification-preferences-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule
],
  template: `
    <h2 mat-dialog-title>Notification Preferences</h2>
    <mat-dialog-content>
      <div class="preferences-container">
        <p class="description">Select which facilities you want to receive notifications for.</p>
    
        <mat-accordion class="companies-accordion">
          @for (company of companies; track company) {
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  <mat-checkbox
                    [(ngModel)]="company.allEnabled"
                    [disabled]="company.facilities.length > 0 && getLockedCount(company) === company.facilities.length"
                    (change)="toggleAllFacilities(company)"
                    (click)="$event.stopPropagation()"
                    class="company-checkbox">
                    {{ company.companyName }}
                  </mat-checkbox>
                </mat-panel-title>
                <mat-panel-description>
                  {{ getEnabledCount(company) }} of {{ company.facilities.length }} facilities enabled
                </mat-panel-description>
              </mat-expansion-panel-header>
              <div class="facilities-list">
                @for (facility of company.facilities; track facility) {
                  <div class="facility-item">
                    <mat-checkbox
                      [(ngModel)]="facility.enabled"
                      [disabled]="facility.locked"
                      (change)="updateCompanyState(company)"
                      class="facility-checkbox">
                      {{ facility.facilityName }}
                    </mat-checkbox>
                  </div>
                }
              </div>
            </mat-expansion-panel>
          }
        </mat-accordion>
    
        @if (companies.length === 0) {
          <div class="no-data">
            <mat-icon>notifications_off</mat-icon>
            <p>No facilities available for notifications.</p>
          </div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button color="primary" (click)="onSave()">
        Save Preferences
      </button>
    </mat-dialog-actions>
    `,
  styleUrl: './notification-preferences-dialog.component.scss'
})
export class NotificationPreferencesDialogComponent implements OnInit {
  companies: CompanyNotification[] = [];
  private dataService = inject(DataService);
  private authService = inject(AuthenticationService);
  private toastr = inject(ToastrService);
  private userId: number = 0;

  constructor(
    public dialogRef: MatDialogRef<NotificationPreferencesDialogComponent>
  ) { }

  async ngOnInit(): Promise<void> {
    // Subscribe to current user
    this.authService.currentUser.subscribe(async userAuth => {
      console.log('Current user auth:', userAuth);
      if (userAuth?.user?.id) {
        this.userId = userAuth.user.id;
        console.log('Loading notification preferences for userId:', this.userId);
        await this.loadNotificationPreferences();
      } else {
        console.warn('No current user found');
      }
    });
  }

  async loadNotificationPreferences(): Promise<void> {
    try {
      console.log('Fetching customer and facilities...');
      // Get customer and facility list
      const customerSummaries: CustomerSummary[] = await firstValueFrom(this.dataService.getCustomerAndFacilities());
      console.log('Customer summaries received:', customerSummaries);
      
      console.log('Fetching user notifications...');
      // Get user's current notification preferences
      const [userNotifications, roles] = await Promise.all([
        firstValueFrom(this.dataService.getUserNotificationsForUserId(this.userId)),
        firstValueFrom(this.dataService.getUserRoles(this.userId))
      ]);
      const lockedFacilityIds = new Set(
        roles
          .filter(role => role.role === UserRoleValue.FacilityAdmin || role.role === UserRoleValue.FacilityUser)
          .map(role => role.facilityId)
          .filter((facilityId): facilityId is number => facilityId != null)
      );
      const notificationsByFacilityId = new Map(userNotifications.map(notification => [notification.facilityId, notification]));
      console.log('User notifications received:', userNotifications);
      
      // Map to notification structure
      this.companies = customerSummaries.map((customer: CustomerSummary) => {
        const companyNotification: CompanyNotification = {
          companyId: customer.id,
          companyName: customer.name || '',
          allEnabled: false,
          facilities: customer.facilities?.map((facility) => {
            const notification = notificationsByFacilityId.get(facility.id);
            const locked = lockedFacilityIds.has(facility.id) || notification?.isRequired === true;
            return {
              facilityId: facility.id,
              facilityName: facility.name || '',
              enabled: locked || notification !== undefined,
              locked
            };
          }) || []
        };
        
        this.updateCompanyState(companyNotification);
        return companyNotification;
      });
      
      console.log('Companies mapped:', this.companies);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      this.toastr.error('Failed to load notification preferences');
    }
  }

  toggleAllFacilities(company: CompanyNotification): void {
    const enabled = company.allEnabled;
    company.facilities.forEach(facility => {
      if (!facility.locked) {
        facility.enabled = enabled;
      }
    });

    this.updateCompanyState(company);
  }

  updateCompanyState(company: CompanyNotification): void {
    const enabledCount = company.facilities.filter(f => f.enabled).length;
    company.allEnabled = enabledCount === company.facilities.length && company.facilities.length > 0;
  }

  getEnabledCount(company: CompanyNotification): number {
    return company.facilities.filter(f => f.enabled).length;
  }

  getLockedCount(company: CompanyNotification): number {
    return company.facilities.filter(f => f.locked).length;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onSave(): Promise<void> {
    try {
      // Collect all enabled facility IDs
      const enabledFacilityIds = this.companies
        .flatMap(company => company.facilities
          .filter(f => f.enabled && !f.locked)
          .map(f => f.facilityId)
        );

      // Update via API
      const success = await firstValueFrom(
        this.dataService.updateUserNotificationsForUserId(this.userId, this.userId, enabledFacilityIds)
      );

      if (success) {
        this.toastr.success('Notification preferences saved successfully');
        this.dialogRef.close(true);
      } else {
        this.toastr.error('Failed to save notification preferences');
      }
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      this.toastr.error('Failed to save notification preferences');
    }
  }
}
